import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SparklineProps {
    values: number[]
    color?: string
    className?: string
}

// Decorative trend line for stat tiles. The value it summarizes is always shown as text.
export const Sparkline: FC<SparklineProps> = ({ values, color = 'var(--chart-1)', className }): ReactNode => {
    if (values.length < 2) return null

    const min = Math.min(...values)
    const max = Math.max(...values)
    const span = max - min || 1
    const last = values.length - 1

    const path = values
        .map((value, index) => {
            const x = (index / last) * 100
            const y = 30 - ((value - min) / span) * 26
            return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`
        })
        .join(' ')

    const endY = 30 - ((values[last] - min) / span) * 26

    return (
        <div className={cn('relative', className)} aria-hidden='true'>
            <svg viewBox='0 0 100 32' preserveAspectRatio='none' className='absolute inset-0 size-full overflow-visible'>
                <path
                    d={path}
                    fill='none'
                    stroke={color}
                    strokeWidth={1.75}
                    strokeLinejoin='round'
                    strokeLinecap='round'
                    vectorEffect='non-scaling-stroke'
                />
            </svg>

            <span
                className='absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card'
                style={{ left: '100%', top: `${((endY / 32) * 100).toFixed(2)}%`, background: color }}
            />
        </div>
    )
}
